/**
 * ADR-118 — SimpleJWT adapter.
 *
 * Covers AIM, SMS, and AIR's anticipated path. Maps the Tiaki Django
 * REST + SimpleJWT backend's response shapes into the canonical
 * `LoginResult<U>` from `@aegix/auth-core`.
 *
 * Endpoints used (per ADR-118 §Cross-platform auth wire contract):
 *   - `POST /rest/sessions/`            — email+password login
 *   - `GET  /api/v2/user/me/`           — the `/me/` probe (issues nothing)
 *   - `GET  /rest/users/me/`            — token-based login; probe fallback
 *   - `POST /api/v2/auth/logout/`       — server-side authoritative logout
 *   - (refresh handled by RefreshAdapter, not this adapter)
 *
 * The adapter does NOT touch localStorage or cookies — those are
 * the provider's responsibility (`libs/auth-core`). This adapter is
 * pure HTTP indirection.
 */

import type { BaseUser, LoginResult, LogoutResult } from '@aegix/auth-core';
import {
  AuthError,
  type LoginAdapter,
  type LoginCredentials,
  type RefreshAdapter,
} from './types';

export interface SimpleJwtAdapterConfig {
  /**
   * Base URL for the backend. Empty string means relative URLs
   * (`/rest/sessions/`), suitable for SPAs served from the same
   * origin as the backend.
   */
  baseUrl?: string;
  /**
   * `fetch` implementation. Defaults to global `fetch`. Tests
   * inject a mock.
   */
  fetchImpl?: typeof fetch;
}

/**
 * The SimpleJWT user shape expected by AIM / SMS / AIR. The lib
 * itself is generic over `U extends BaseUser`; this is the typical
 * shape consumers will use. Apps can pass a wider type if they
 * want to thread additional response fields through.
 */
export interface SimpleJwtUser extends BaseUser {
  first_name: string;
  last_name: string;
  schools?: number[];
  vendor?: number | null;
  is_vendor_user?: boolean;
  permissions?: string[];
  permissions_version?: number;
  /** Additional fields are passed through opaque. */
  [key: string]: unknown;
}

/**
 * The current-user read that issues nothing — no `jwt`, no `refresh_token`,
 * no minted refresh token, no auth cookie (ADR-118's "reads do not issue
 * credentials" amendment). Preferred for every probe.
 */
const CURRENT_USER_PATH = '/api/v2/user/me/';

/**
 * The older read. Still mints a refresh token on every call, which is why the
 * probe only falls back to it when the backend has no credential-free read.
 */
const LEGACY_CURRENT_USER_PATH = '/rest/users/me/';

/**
 * Statuses that mean "this backend does not serve that path". Anything else —
 * 401 above all — is the answer to the question, not a reason to ask a
 * different endpoint the same one.
 */
const ENDPOINT_ABSENT_STATUSES: ReadonlySet<number> = new Set([404, 405, 501]);

/**
 * Query marker by which a caller declares the legacy read a credential path —
 * "I am here for the tokens, not the user". Only a bootstrap that has no
 * refresh token of its own may use it; the backend records who does, and the
 * default stops emitting credentials once nobody receives them without asking
 * (ADR-118 amendment).
 */
const CREDENTIAL_OPT_IN = 'issue_credentials=1';

export function createSimpleJwtAdapter<
  U extends BaseUser = SimpleJwtUser,
>(
  config: SimpleJwtAdapterConfig = {},
): LoginAdapter<U> {
  const fetchImpl = config.fetchImpl ?? globalThis.fetch;
  const baseUrl = config.baseUrl ?? '';

  /**
   * Whether this backend serves the credential-free read. `undefined` until a
   * call finds out. Sticky once answered: re-probing a backend that has
   * already said no costs an extra round-trip on every probe, and a deploy
   * that adds the endpoint is picked up on the next page load.
   */
  let servesCredentialFreeRead: boolean | undefined;

  /**
   * Read the current user, preferring the endpoint that issues nothing.
   *
   * Shared by the boot probe and the SSO bootstrap: both want the user and
   * neither wants a token out of it, so neither may carry the credential
   * marker. The bootstrap takes its tokens from the refresh endpoint.
   */
  async function readCurrentUser(
    token: string | null | undefined,
    messageFor: (status: number) => string,
  ): Promise<U> {
      // Boot reconcile probe. Prefer the stored access
      // token as the Bearer credential — it's the one that works
      // in-session, and the host-only `jwt-auth` cookie isn't sent when
      // the app host ≠ API host. Fall back to `credentials: 'include'`
      // (cookie) for the cookie-only purgatory where no token
      // is in storage.
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `JWT ${token}`;
      }
      const init: RequestInit = { credentials: 'include', headers };

      // A read should not hand out credentials. The two paths return the same
      // user, so which one answered is invisible to the caller — and a
      // backend without the newer path is a deployment state, not an error.
      if (servesCredentialFreeRead !== false) {
        const response = await fetchImpl(`${baseUrl}${CURRENT_USER_PATH}`, init);

        if (response.ok) {
          servesCredentialFreeRead = true;
          return (await response.json()) as U;
        }

        if (!ENDPOINT_ABSENT_STATUSES.has(response.status)) {
          throw new AuthError(
            messageFor(response.status),
            response.status === 401 ? 'invalid_credentials' : 'server',
            await safeJson(response),
          );
        }

        servesCredentialFreeRead = false;
      }

      const response = await fetchImpl(
        `${baseUrl}${LEGACY_CURRENT_USER_PATH}`,
        init,
      );

      if (!response.ok) {
        throw new AuthError(
          messageFor(response.status),
          response.status === 401 ? 'invalid_credentials' : 'server',
          await safeJson(response),
        );
      }

      const data = (await response.json()) as U;
      return data;
  }

  return {
    async login(credentials: LoginCredentials): Promise<LoginResult<U>> {
      const response = await fetchImpl(`${baseUrl}/rest/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
        }),
      });

      if (!response.ok) {
        await throwLoginError(response);
      }

      const data = (await response.json()) as Record<string, unknown>;
      return unpackLoginResult<U>(data);
    },

    async loginWithToken(jwt: string): Promise<LoginResult<U>> {
      // SSO popup return flow: store the JWT, then fetch /me/.
      //
      // The one caller that reads this endpoint *for* its credentials, so it
      // says so. It holds a popup-provided access token and no refresh token;
      // the legacy read is where the session gets anchored. Everything else
      // here reads the credential-free endpoint.
      const response = await fetchImpl(
        `${baseUrl}${LEGACY_CURRENT_USER_PATH}?${CREDENTIAL_OPT_IN}`,
        {
          headers: {
            Authorization: `JWT ${jwt}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new AuthError(
          'Failed to fetch user profile after token login',
          response.status >= 500 ? 'server' : 'invalid_credentials',
          await safeJson(response),
        );
      }

      // `/rest/users/me/` (UserSerializer) carries the durable
      // `refresh_token` (and `permissions_version`). Capture them via the
      // same unpack path as email+password login, passing the popup-
      // provided `jwt` as the authoritative access token. This establishes
      // the app's OWN per-origin refresh token so the boot-refresh anchor
      // keeps the long-lived session alive independent of the cross-app
      // shared `jwt-auth` cookie. Discarding the refresh token here would
      // strand SSO-authenticated users on the shared cookie — which a
      // short-lived SMS role-user session clobbers, collapsing AIM's
      // year-long session to SMS's few-hours lifetime.
      const data = (await response.json()) as Record<string, unknown>;
      return unpackLoginResult<U>(data, jwt);
    },

    async bootstrapFromSso(): Promise<LoginResult<U>> {
      // ADR-118 Phase 2: silent cross-app SSO bootstrap. No
      // password — trade the parent-domain, HttpOnly `aegix-sso` assertion
      // (sent automatically via `credentials: 'include'`; JS never reads it)
      // for a per-app access + durable refresh token, then fetch the user.
      // POSTs the canonical refresh endpoint with NO body so the backend
      // (`TokenRefreshWithTokenView`) falls to the `aegix-sso`
      // cookie source (expiry enforced server-side) and returns
      // `{ token, refresh_token }`.
      const bootResponse = await fetchImpl(
        `${baseUrl}/api/v2/api-token-refresh/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({}),
        },
      );

      if (!bootResponse.ok) {
        await throwLoginError(bootResponse);
      }

      const bootData = (await bootResponse.json()) as {
        token?: string;
        jwt?: string;
        refresh_token?: string;
      };
      const token = bootData.token ?? bootData.jwt;
      if (!token) {
        throw new AuthError(
          'SSO bootstrap response missing token field',
          'server',
          bootData,
        );
      }

      // Fetch the user with the freshly-minted access token, then assemble the
      // LoginResult. The refresh token comes from the BOOTSTRAP response,
      // which preserves the role-user `orig_iat` anchor; a token minted by the
      // user read would reset that window and defeat the server-side clamp.
      // So this is a read, and it takes the endpoint that issues nothing —
      // asking the legacy one for credentials it then discards is what kept
      // the default from ever being safe to change.
      const meData = (await readCurrentUser(
        token,
        () => 'Failed to fetch user profile after SSO bootstrap',
      )) as Record<string, unknown>;
      return unpackLoginResult<U>(meData, token, bootData.refresh_token);
    },

    async logout(_jwt: string | null): Promise<LogoutResult> {
      // Server-side authoritatively clears the
      // jwt-auth cookie via Set-Cookie: Max-Age=0 on every candidate
      // Domain. We don't need to attach the JWT explicitly — the
      // browser sends the cookie automatically (`credentials: 'include'`).
      const response = await fetchImpl(`${baseUrl}/api/v2/auth/logout/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        // 401 here is fine (cookie already expired). Other errors
        // propagate — provider will catch and continue local cleanup.
        if (response.status === 401) {
          return { ok: true };
        }
        throw new AuthError(
          `Server-side logout failed with status ${response.status}`,
          response.status >= 500 ? 'server' : 'unknown',
          await safeJson(response),
        );
      }

      return (await response.json()) as LogoutResult;
    },

    async fetchMe(token?: string | null): Promise<U> {
      return readCurrentUser(
        token,
        (status) => `fetchMe probe failed with status ${status}`,
      );
    },

    async updateLanguage(
      userId: string | number,
      language: string,
      token?: string | null,
    ): Promise<U> {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `JWT ${token}`;
      }
      const response = await fetchImpl(`${baseUrl}/api/v2/user/${userId}/`, {
        method: 'PATCH',
        credentials: 'include',
        headers,
        body: JSON.stringify({ language }),
      });
      if (!response.ok) {
        throw new AuthError(
          `Failed to update language (status ${response.status})`,
          response.status === 401 ? 'invalid_credentials' : 'server',
          await safeJson(response),
        );
      }
      const data = (await response.json()) as U;
      return data;
    },
  };
}

/**
 * Companion refresh adapter for the SimpleJWT backend.
 *
 * Endpoint: `POST /api/v2/api-token-refresh/` — Tiaki's canonical
 * refresh URL (`TokenRefreshWithTokenView` in
 * `server.api.v2.views`). Accepts a SimpleJWT-format refresh JWT
 * in either the `token` or `refresh` body field; returns
 * `{ token, jwt, refresh_token }` (the serializer aliases the new
 * access JWT into both `token` and `jwt` for legacy consumers).
 *
 * AIM, SMS and ONE all wire this factory, so the URL + response shape
 * are pinned here once. A wrong URL fails silently: the 404 degrades
 * boot-time refresh to the cookie probe, so users stay authenticated
 * via cookie while the JWT-refresh path itself never runs.
 */
export function createSimpleJwtRefreshAdapter(
  config: SimpleJwtAdapterConfig = {},
): RefreshAdapter {
  const fetchImpl = config.fetchImpl ?? globalThis.fetch;
  const baseUrl = config.baseUrl ?? '';

  return {
    async refresh(refreshToken: string) {
      const response = await fetchImpl(
        `${baseUrl}/api/v2/api-token-refresh/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          // Backend accepts `token` (preferred) or `refresh`. We send
          // `refresh` to retain the SimpleJWT-standard field name in
          // the request body — easier to read against external docs
          // — and the serializer normalizes both spellings.
          body: JSON.stringify({ refresh: refreshToken }),
        },
      );

      if (!response.ok) {
        throw new AuthError(
          `Token refresh failed with status ${response.status}`,
          response.status === 401 ? 'invalid_credentials' : 'server',
          await safeJson(response),
        );
      }

      const data = (await response.json()) as {
        token?: string;
        access?: string;
        jwt?: string;
        permissions_version?: number;
      };
      // Tiaki's TokenRefreshSerializer returns `token` AND `jwt`
      // (same value, dual-keyed for legacy compat). Stock SimpleJWT
      // would return `access`. Accept all three so this adapter
      // stays forward-compatible if the serializer ever drops a key.
      const jwt = data.token ?? data.access ?? data.jwt;
      if (!jwt) {
        throw new AuthError(
          'Refresh response missing token/access/jwt field',
          'server',
          data,
        );
      }
      return {
        jwt,
        permissionsVersion: data.permissions_version,
      };
    },
  };
}

// ---- helpers ----

function unpackLoginResult<U extends BaseUser>(
  data: Record<string, unknown>,
  jwtOverride?: string,
  refreshTokenOverride?: string,
): LoginResult<U> {
  // Bracket-notation access satisfies `noPropertyAccessFromIndexSignature`
  // (enabled by AIM's tsconfig); the runtime shape is
  // identical to dot-notation for consumers without that flag.
  //
  // `jwtOverride` serves the SSO path (`loginWithToken`), where
  // the access JWT arrives from the popup rather than the `/me/` body —
  // but that body still carries the durable `refresh_token` +
  // `permissions_version` we need to capture. Email+password login passes
  // no override and reads `jwt` from the body.
  //
  // `refreshTokenOverride` serves `bootstrapFromSso`, where the
  // durable refresh token comes from the bootstrap response (orig_iat-anchored)
  // rather than the `/me/` body — capturing /me/'s freshly-minted refresh
  // token would reset a role user's window.
  const jwt = jwtOverride ?? (data['jwt'] as string | undefined);
  const refreshToken =
    refreshTokenOverride ?? (data['refresh_token'] as string | undefined);
  if (!jwt) {
    throw new AuthError(
      'Login response missing jwt field',
      'server',
      data,
    );
  }
  // Strip the jwt + refresh_token from the user shape; the rest IS
  // the user. This matches the v2 UserSerializer wire contract.
  const { jwt: _j, refresh_token: _r, permissions_version, ...userFields } =
    data;
  void _j;
  void _r;
  return {
    user: userFields as unknown as U,
    jwt,
    refreshToken,
    permissionsVersion:
      typeof permissions_version === 'number' ? permissions_version : undefined,
  };
}

async function throwLoginError(response: Response): Promise<never> {
  const body = (await safeJson(response)) as
    | { non_field_errors?: string[]; detail?: string }
    | null;
  const message =
    body?.non_field_errors?.[0] ?? body?.detail ?? 'Login failed';
  let kind: AuthError['kind'] = 'unknown';
  if (response.status === 423) kind = 'account_locked';
  else if (response.status === 401) kind = 'invalid_credentials';
  else if (response.status === 400) kind = 'invalid_credentials';
  else if (response.status >= 500) kind = 'server';
  throw new AuthError(message, kind, body);
}

async function safeJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
